import _ from 'lodash';
import { AnyAction } from 'redux';
import IDocument from '~Api/Loan/IDocument';
import IHistory from '~Api/Loan/IHistory';
import ILoan from '~Api/Loan/ILoan';
import ILoanFee from '~Api/Loan/ILoanFee';
import ILoanGracePeriod from '~Api/Loan/ILoanGracePeriod';
import ILoanNote from '~Api/Loan/ILoanNote';
import ILoanPayoutFigure from '~Api/Loan/ILoanPayoutFigure';
import ILoanPayoutFigureItem from '~Api/Loan/ILoanPayoutFigureItem';
import ILoanPayoutFigureSection from '~Api/Loan/ILoanPayoutFigureSection';
import ILoanTransaction from '~Api/Loan/ILoanTransaction';
import LoanStatusEnum from '~Api/Loan/LoanStatusEnum';
import IWarehouseLoan from '~Api/Warehouse/IWarehouseLoan';
import { IDictionary } from '~utilities/IDictionary';
import {
    ILoanDischargeAction,
    ILoanDischargeDateAction,
    ILoanDocumentRemoveAction,
    ILoanDocumentSetAction,
    ILoanDocumentsSetAction,
    ILoanDrawdownInternalCommentsAction,
    ILoanDrawdownItemRemoveAction,
    ILoanDrawdownItemSetAction,
    ILoanDrawdownItemValueSetAction,
    ILoanDrawdownSetAction,
    ILoanDrawdownValueSetAction,
    ILoanDrawdownsSetAction,
    ILoanFeeRemoveAction,
    ILoanFeeSetAction,
    ILoanFeeValueSetAction,
    ILoanFeesSetAction,
    ILoanGracePeriodSetAction,
    ILoanGracePeriodsSetAction,
    ILoanHistoriesSetAction,
    ILoanInternalCommentsAction,
    ILoanInvestmentsSetAction,
    ILoanListSettingsSetAction,
    ILoanNoteSetAction,
    ILoanNotesSetAction,
    ILoanPayoutFigureCalculationSetAction,
    ILoanPayoutFigureItemDeleteAction,
    ILoanPayoutFigureItemSetAction,
    ILoanPayoutFigureItemValueSetAction,
    ILoanPayoutFigureRemoveAction,
    ILoanPayoutFigureSectionDeleteAction,
    ILoanPayoutFigureSectionSetAction,
    ILoanPayoutFigureSetAction,
    ILoanPayoutFigureValueSetAction,
    ILoanPayoutFiguresSetAction,
    ILoanSetAction,
    ILoanTransactionsSetAction,
    ILoanValueSetAction,
    ILoanWarehouseLoansSetAction,
    ILoansDashboardSetAction,
    ILoansDischargeForecastSetAction,
    ILoansDrawdownsPendingSetAction,
    ILoansDrawdownsSetAction,
    ILoansOtherRetainedFundsSetAction,
    ILoansPaginatedSetAction,
    ILoansSearchResultsSetAction,
} from './actions';
import LoansActionsEnum from './ActionsEnum';
import { ILoanListSettings } from './List';
import WarehousesActionsEnum from '~Warehouses/ActionsEnum';
import {
    IWarehouseEligibleLoansSetAction,
    IWarehouseLoanSetAction,
    IWarehouseLoansSetAction,
    IWarehousePendingDrawdownsSetAction,
    IWarehouseProposedSalesPendingSetAction,
} from '~Warehouses/actions';
import IWarehouseEligibleLoan from '~Api/Warehouse/IWarehouseEligibleLoan';
import ILoanDrawdown from '~Api/Loan/ILoanDrawdown';
import ILoanDrawdownItem from '~Api/Loan/ILoanDrawdownItem';
import IInvestment from '~Api/Investment/IInvestment';
import IWarehouseProposedSale from '~Api/Warehouse/IWarehouseProposedSale';
import ILoanPayoutFigureCalculation from '~Api/Loan/ILoanPayoutFigureCalculation';

export interface ILoansState {
    documents: IDictionary<IDocument>;
    drawdownItems: IDictionary<ILoanDrawdownItem>;
    drawdownPendingUuids: string[];
    drawdowns: IDictionary<ILoanDrawdown>;
    drawdownUuids: string[];
    fees: IDictionary<IDictionary<ILoanFee>>;
    gracePeriods: IDictionary<ILoanGracePeriod>;
    histories: IDictionary<IDictionary<IHistory>>;
    loanDocumentUuids: IDictionary<string[]>;
    loanDrawdownItemUuids: IDictionary<string[]>;
    loanDrawdownUuids: IDictionary<string[]>;
    loanGracePeriodUuids: IDictionary<string[]>;
    loanInvestmentUuids: IDictionary<string[]>;
    loanListSettings: ILoanListSettings;
    loanPayoutFigureUuids: IDictionary<string[]>;
    loans: IDictionary<ILoan>;
    loansDashboardUuids: string[];
    loansDischargeForecastUuids: string[];
    loansPaginatedCount: number;
    loansPaginatedLoading: boolean;
    loansPaginatedUuids: string[];
    loansSearchResultUuids: string[];
    loanTransactions: IDictionary<ILoanTransaction[]>;
    loanWarehouseLoanUuids: IDictionary<string[]>;
    notes: IDictionary<IDictionary<ILoanNote>>;
    otherRetainedFundsUuids: string[];
    payoutFigureCalculations: IDictionary<IDictionary<ILoanPayoutFigureCalculation>>;
    payoutFigureItems: IDictionary<ILoanPayoutFigureItem>;
    payoutFigures: IDictionary<ILoanPayoutFigure>;
    payoutFigureSectionItemUuids: IDictionary<string[]>;
    payoutFigureSections: IDictionary<ILoanPayoutFigureSection>;
    payoutFigureSectionUuids: IDictionary<string[]>;
}

const initialData: ILoansState = {
    documents: {},
    drawdownItems: {},
    drawdownPendingUuids: null,
    drawdownUuids: null,
    drawdowns: {},
    fees: {},
    gracePeriods: {},
    histories: {},
    loanDocumentUuids: {},
    loanDrawdownItemUuids: {},
    loanDrawdownUuids: {},
    loanGracePeriodUuids: {},
    loanInvestmentUuids: {},
    loanListSettings: {
        administratorUuidFilter: null,
        codeSearch: null,
        currentPage: 1,
        nameSearch: null,
        pageSize: 20,
        sortField: 'startDate',
        sortOrder: 'descend',
        statusFilter: [
            LoanStatusEnum.ActiveBadStanding,
            LoanStatusEnum.ActiveGoodStanding,
            LoanStatusEnum.ActiveMatured,
            LoanStatusEnum.ClosedObligationsMet,
            LoanStatusEnum.ClosedRefinanced,
            LoanStatusEnum.ClosedWrittenOff,
        ],
    },
    loanPayoutFigureUuids: {},
    loanTransactions: {},
    loanWarehouseLoanUuids: {},
    loans: {},
    loansDashboardUuids: null,
    loansDischargeForecastUuids: null,
    loansPaginatedCount: null,
    loansPaginatedLoading: false,
    loansPaginatedUuids: null,
    loansSearchResultUuids: null,
    notes: {},
    otherRetainedFundsUuids: null,
    payoutFigureCalculations: {},
    payoutFigureItems: {},
    payoutFigureSectionItemUuids: {},
    payoutFigureSectionUuids: {},
    payoutFigureSections: {},
    payoutFigures: {},
};

function statifyLoan(state: ILoansState, loan: ILoan): ILoansState {
    const clonedState: ILoansState = { ...state };

    if (loan.warehouseLoans) {
        clonedState.loanWarehouseLoanUuids[loan.uuid] = _.map(loan.warehouseLoans, (warehouseLoan: IWarehouseLoan) => warehouseLoan.uuid);
    }

    clonedState.loans[loan.uuid] = _.omit(loan, [
        'application',
        'extensionApplication',
        'warehouseLoans',
    ]);

    return clonedState;
}

function statifyLoans(state: ILoansState, loans: ILoan[]): ILoansState {
    let clonedState: ILoansState = { ...state };

    loans.forEach((loan: ILoan) => {
        clonedState = statifyLoan(clonedState, loan);
    });

    return clonedState;
}

function statifyLoanDrawdowns(state: ILoansState, loanUuid: string, loanDrawdowns: ILoanDrawdown[]): ILoansState {
    let clonedState: ILoansState = { ...state };

    const loanDrawdownUuids: string[] = [];
    loanDrawdowns.forEach((loanDrawdown: ILoanDrawdown) => {
        clonedState = statifyLoanDrawdown(clonedState, loanDrawdown);
        loanDrawdownUuids.push(loanDrawdown.uuid);
    });

    return {
        ...clonedState,
        loanDrawdownUuids: {
            ...clonedState.loanDrawdownUuids,
            [loanUuid]: loanDrawdownUuids,
        },
    };
}

function statifyDrawdowns(state: ILoansState, loanDrawdowns: ILoanDrawdown[]): ILoansState {
    let clonedState: ILoansState = { ...state };

    loanDrawdowns.forEach((loanDrawdown: ILoanDrawdown) => {
        clonedState = statifyLoanDrawdown(clonedState, loanDrawdown);
    });

    return clonedState;
}

function statifyLoanDrawdown(state: ILoansState, loanDrawdown: ILoanDrawdown): ILoansState {
    let clonedState: ILoansState = { ...state };

    if (loanDrawdown.loan) {
        clonedState = statifyLoan(clonedState, loanDrawdown.loan);
    }

    const loanDrawdownItemUuids: string[] = [];
    _.each(loanDrawdown.items, (item: ILoanDrawdownItem) => {
        clonedState.drawdownItems[item.uuid] = item;
        loanDrawdownItemUuids.push(item.uuid);
    });

    return {
        ...clonedState,
        drawdowns: {
            ...clonedState.drawdowns,
            [loanDrawdown.uuid]: _.omit(loanDrawdown, [ 'items', 'loan' ]),
        },
        loanDrawdownItemUuids: {
            ...clonedState.loanDrawdownItemUuids,
            [loanDrawdown.uuid]: loanDrawdownItemUuids,
        },
    };
}

function dehydratePayoutFigure(payoutFigure: ILoanPayoutFigure): ILoanPayoutFigure {
    return _.omit(payoutFigure, [ 'sections' ]);
}

function dehydratePayoutFigureSection(payoutFigureSection: ILoanPayoutFigureSection): ILoanPayoutFigureSection {
    return _.omit(payoutFigureSection, [ 'items' ]);
}

export function loansReducer(state: ILoansState = initialData, action: AnyAction): ILoansState {
    switch (action.type) {
        case LoansActionsEnum.LoanDocumentsSet: {
            const typedAction: ILoanDocumentsSetAction = action as ILoanDocumentsSetAction;

            const documents: IDictionary<IDocument> = {};
            const loanDocumentUuids: string[] = [];
            typedAction.documents.forEach((document: IDocument) => {
                documents[document.uuid] = document;
                loanDocumentUuids.push(document.uuid);
            });

            return {
                ...state,
                documents: {
                    ...state.documents,
                    ...documents,
                },
                loanDocumentUuids: {
                    ...state.loanDocumentUuids,
                    [typedAction.loanUuid]: loanDocumentUuids,
                },
            };
        }

        case LoansActionsEnum.LoanDocumentRemove: {
            const typedAction: ILoanDocumentRemoveAction = action as ILoanDocumentRemoveAction;

            const loanDocument: IDocument = state.documents[typedAction.documentUuid];

            return {
                ...state,
                documents: _.omit(state.documents, typedAction.documentUuid),
                loanDocumentUuids: {
                    ...state.loanDocumentUuids,
                    [loanDocument.loanUuid]: _.filter(state.loanDocumentUuids[loanDocument.loanUuid], (documentUuid: string) => documentUuid !== typedAction.documentUuid),
                },
            };
        }

        case LoansActionsEnum.LoanDocumentSet: {
            const typedAction: ILoanDocumentSetAction = action as ILoanDocumentSetAction;

            return {
                ...state,
                documents: {
                    ...state.documents,
                    [typedAction.document.uuid]: typedAction.document,
                },
                loanDocumentUuids: {
                    ...state.loanDocumentUuids,
                    [typedAction.document.loanUuid]: _.uniq([
                        ...(state.loanDocumentUuids[typedAction.document.loanUuid] || []),
                        typedAction.document.uuid,
                    ]),
                },
            };
        }

        case LoansActionsEnum.LoanDischarge: {
            const typedAction: ILoanDischargeAction = action as ILoanDischargeAction;

            return {
                ...state,
                loans: {
                    ...state.loans,
                    [typedAction.uuid]: {
                        ...state.loans[typedAction.uuid],
                        dischargeDate: typedAction.dischargeDate,
                    },
                },
            };
        }

        case LoansActionsEnum.LoanDischargeDate: {
            const typedAction: ILoanDischargeDateAction = action as ILoanDischargeDateAction;

            return {
                ...state,
                loans: {
                    ...state.loans,
                    [typedAction.uuid]: {
                        ...state.loans[typedAction.uuid],
                        dischargeDate: typedAction.dischargeDate,
                    },
                },
            };
        }

        case LoansActionsEnum.LoanDrawdownValueSet: {
            const typedAction: ILoanDrawdownValueSetAction = action as ILoanDrawdownValueSetAction;

            return {
                ...state,
                drawdowns: {
                    ...state.drawdowns,
                    [typedAction.drawdownUuid]: {
                        ...state.drawdowns[typedAction.drawdownUuid],
                        [typedAction.key]: typedAction.value,
                    },
                },
            };
        }

        case LoansActionsEnum.LoanDrawdownItemSet: {
            const typedAction: ILoanDrawdownItemSetAction = action as ILoanDrawdownItemSetAction;

            return {
                ...state,
                drawdownItems: {
                    ...state.drawdownItems,
                    [typedAction.drawdownItem.uuid]: typedAction.drawdownItem,
                },
                loanDrawdownItemUuids: {
                    ...state.loanDrawdownItemUuids,
                    [typedAction.drawdownUuid]: _.uniq([
                        ...state.loanDrawdownItemUuids[typedAction.drawdownUuid],
                        typedAction.drawdownItem.uuid,
                    ]),
                },
            };
        }

        case LoansActionsEnum.LoanDrawdownItemRemove: {
            const typedAction: ILoanDrawdownItemRemoveAction = action as ILoanDrawdownItemRemoveAction;
            const drawdownUuid: string = state.drawdownItems[typedAction.itemUuid].loanDrawdownUuid;

            return {
                ...state,
                drawdownItems: _.omit(state.drawdownItems, typedAction.itemUuid),
                loanDrawdownItemUuids: {
                    ...state.loanDrawdownItemUuids,
                    [drawdownUuid]: _.filter(state.loanDrawdownItemUuids[drawdownUuid], (itemUuid: string) => itemUuid !== typedAction.itemUuid),
                },
            };
        }

        case LoansActionsEnum.LoanDrawdownItemValueSet: {
            const typedAction: ILoanDrawdownItemValueSetAction = action as ILoanDrawdownItemValueSetAction;

            return {
                ...state,
                drawdownItems: {
                    ...state.drawdownItems,
                    [typedAction.itemUuid]: {
                        ...state.drawdownItems[typedAction.itemUuid],
                        [typedAction.key]: typedAction.value,
                    },
                },
            };
        }

        case LoansActionsEnum.LoanDrawdownSet: {
            const typedAction: ILoanDrawdownSetAction = action as ILoanDrawdownSetAction;

            return {
                ...state,
                ...statifyLoanDrawdown(state, typedAction.drawdown),
            };
        }

        case LoansActionsEnum.LoanDrawdownsSet: {
            const typedAction: ILoanDrawdownsSetAction = action as ILoanDrawdownsSetAction;

            return {
                ...state,
                ...statifyLoanDrawdowns(state, typedAction.loanUuid, typedAction.drawdowns),
            };
        }

        case LoansActionsEnum.LoanFeeRemove: {
            const typedAction: ILoanFeeRemoveAction = action as ILoanFeeRemoveAction;

            return {
                ...state,
                fees: {
                    ...state.fees,
                    [typedAction.loanUuid]: _.omit(state.fees[typedAction.loanUuid] || {}, typedAction.feeUuid),
                },
            };
        }

        case LoansActionsEnum.LoanFeeSet: {
            const typedAction: ILoanFeeSetAction = action as ILoanFeeSetAction;

            return {
                ...state,
                fees: {
                    ...state.fees,
                    [typedAction.loanUuid]: {
                        ...state.fees[typedAction.loanUuid],
                        [typedAction.fee.uuid]: typedAction.fee,
                    },
                },
            };
        }

        case LoansActionsEnum.LoanFeeValueSet: {
            const typedAction: ILoanFeeValueSetAction = action as ILoanFeeValueSetAction;

            return {
                ...state,
                fees: {
                    ...state.fees,
                    [typedAction.loanUuid]: {
                        ...state.fees[typedAction.loanUuid],
                        [typedAction.feeUuid]: {
                            ...state.fees[typedAction.loanUuid][typedAction.feeUuid],
                            [typedAction.key]: typedAction.value,
                        },
                    },
                },
            };
        }

        case LoansActionsEnum.LoanFeesSet: {
            const typedAction: ILoanFeesSetAction = action as ILoanFeesSetAction;

            return {
                ...state,
                fees: {
                    ...state.fees,
                    [typedAction.loanUuid]: _.keyBy(typedAction.fees, 'uuid'),
                },
            };
        }

        case LoansActionsEnum.LoanGracePeriodSet: {
            const typedAction: ILoanGracePeriodSetAction = action as ILoanGracePeriodSetAction;

            return {
                ...state,
                gracePeriods: {
                    ...state.gracePeriods,
                    [typedAction.loanGracePeriod.uuid]: typedAction.loanGracePeriod,
                },
                loanGracePeriodUuids: {
                    ...state.loanGracePeriodUuids,
                    [typedAction.loanGracePeriod.loanUuid]: _.uniq([
                        ...state.loanGracePeriodUuids[typedAction.loanGracePeriod.loanUuid],
                        typedAction.loanGracePeriod.uuid,
                    ]),
                },
            };
        }

        case LoansActionsEnum.LoanGracePeriodsSet: {
            const typedAction: ILoanGracePeriodsSetAction = action as ILoanGracePeriodsSetAction;

            const gracePeriods: IDictionary<ILoanGracePeriod> = { ...state.gracePeriods };
            const loanGracePeriodUuids: string[] = [];

            typedAction.gracePeriods.forEach((gracePeriod: ILoanGracePeriod) => {
                gracePeriods[gracePeriod.uuid] = gracePeriod;
                loanGracePeriodUuids.push(gracePeriod.uuid);
            });

            return {
                ...state,
                gracePeriods,
                loanGracePeriodUuids: {
                    ...state.loanGracePeriodUuids,
                    [action.loanUuid]: loanGracePeriodUuids,
                },
            };
        }

        case LoansActionsEnum.LoanHistoriesSet: {
            const typedAction: ILoanHistoriesSetAction = action as ILoanHistoriesSetAction;

            return {
                ...state,
                histories: {
                    ...state.histories,
                    [typedAction.uuid]: _.keyBy(typedAction.histories, 'uuid'),
                },
            };
        }

        case LoansActionsEnum.LoanInvestmentsSet: {
            const typedAction: ILoanInvestmentsSetAction = action as ILoanInvestmentsSetAction;

            const loanInvestmentUuids: string[] = [];
            typedAction.investments.forEach((investment: IInvestment) => {
                loanInvestmentUuids.push(investment.uuid);
            });

            return {
                ...state,
                loanInvestmentUuids: {
                    ...state.loanInvestmentUuids,
                    [typedAction.loanUuid]: loanInvestmentUuids,
                },
            };
        }

        case LoansActionsEnum.LoanListSettingsSet: {
            const typedAction: ILoanListSettingsSetAction = action as ILoanListSettingsSetAction;

            return {
                ...state,
                loanListSettings: typedAction.settings,
            };
        }

        case LoansActionsEnum.LoanNoteSet: {
            const typedAction: ILoanNoteSetAction = action as ILoanNoteSetAction;

            return {
                ...state,
                notes: {
                    ...state.notes,
                    [typedAction.loanUuid]: {
                        ...(state.notes[typedAction.loanUuid] || {}),
                        [typedAction.note.uuid]: typedAction.note,
                    },
                },
            };
        }

        case LoansActionsEnum.LoanNotesSet: {
            const typedAction: ILoanNotesSetAction = action as ILoanNotesSetAction;

            return {
                ...state,
                notes: {
                    ...state.notes,
                    [typedAction.uuid]: _.keyBy(typedAction.notes, 'uuid'),
                },
            };
        }

        case LoansActionsEnum.LoanSet: {
            const typedAction: ILoanSetAction = action as ILoanSetAction;

            return {
                ...state,
                ...statifyLoan(state, typedAction.loan),
            };
        }

        case LoansActionsEnum.LoanPayoutFigureCalculationSet: {
            const typedAction: ILoanPayoutFigureCalculationSetAction = action as ILoanPayoutFigureCalculationSetAction;

            return {
                ...state,
                payoutFigureCalculations: {
                    ...state.payoutFigureCalculations,
                    [typedAction.loanUuid]: {
                        ...state.payoutFigureCalculations[typedAction.loanUuid],
                        [typedAction.dischargeDate]: typedAction.loanPayoutFigureCalculation,
                    },
                },
            };
        }

        case LoansActionsEnum.LoanPayoutFigureRemove: {
            const typedAction: ILoanPayoutFigureRemoveAction = action as ILoanPayoutFigureRemoveAction;

            const loanUuid: string = state.payoutFigures[typedAction.payoutFigureUuid].loanUuid;
            const payoutFigureSections: IDictionary<ILoanPayoutFigureSection> = { ...state.payoutFigureSections };
            const payoutFigureItems: IDictionary<ILoanPayoutFigureItem> = { ...state.payoutFigureItems };
            const payoutFigureSectionItemUuids: IDictionary<string[]> = { ...state.payoutFigureSectionItemUuids };
            const payoutFigureSectionUuids: IDictionary<string[]> = { ...state.payoutFigureSectionUuids };

            _.forEach(payoutFigureSectionUuids[typedAction.payoutFigureUuid], (sectionUuid: string) => {
                _.forEach(payoutFigureSectionItemUuids[sectionUuid], (itemUuid: string) => {
                    delete payoutFigureItems[itemUuid];
                });
                delete payoutFigureSections[sectionUuid];
                delete payoutFigureSectionItemUuids[sectionUuid];
            });
            delete payoutFigureSectionUuids[typedAction.payoutFigureUuid];

            return {
                ...state,
                loanPayoutFigureUuids: {
                    ...state.loanPayoutFigureUuids,
                    [loanUuid]: _.filter(state.loanPayoutFigureUuids[loanUuid], (payoutFigureUuid: string) => payoutFigureUuid !== typedAction.payoutFigureUuid),
                },
                payoutFigureItems,
                payoutFigureSectionItemUuids,
                payoutFigureSectionUuids,
                payoutFigureSections,
                payoutFigures: _.omit(state.payoutFigures, typedAction.payoutFigureUuid),
            };
        }

        case LoansActionsEnum.LoanPayoutFigureSet: {
            const typedAction: ILoanPayoutFigureSetAction = action as ILoanPayoutFigureSetAction;

            const loanPayoutFigureUuids: string[] = state.loanPayoutFigureUuids[typedAction.payoutFigure.loanUuid] || [];
            const payoutFigureItems: IDictionary<ILoanPayoutFigureItem> = { ...state.payoutFigureItems };
            const payoutFigureSectionItemUuids: IDictionary<string[]> = { ...state.payoutFigureSectionItemUuids };
            const payoutFigureSectionUuids: string[] = [];
            const payoutFigureSections: IDictionary<ILoanPayoutFigureSection> = { ...state.payoutFigureSections };

            _.forEach(typedAction.payoutFigure.sections, (section: ILoanPayoutFigureSection) => {
                payoutFigureSectionItemUuids[section.uuid] = [];
                _.forEach(section.items, (item: ILoanPayoutFigureItem) => {
                    payoutFigureItems[item.uuid] = {
                        ...item,
                    };
                    payoutFigureSectionItemUuids[section.uuid].push(item.uuid);
                });
                payoutFigureSections[section.uuid] = {
                    ...dehydratePayoutFigureSection(section),
                };
                payoutFigureSectionUuids.push(section.uuid);
            });

            loanPayoutFigureUuids.push(typedAction.payoutFigure.uuid);

            return {
                ...state,
                loanPayoutFigureUuids: {
                    ...state.loanPayoutFigureUuids,
                    [typedAction.payoutFigure.loanUuid]: _.uniq(loanPayoutFigureUuids),
                },
                payoutFigureItems,
                payoutFigureSectionItemUuids,
                payoutFigureSectionUuids: {
                    ...state.payoutFigureSectionUuids,
                    [typedAction.payoutFigure.uuid]: _.uniq(payoutFigureSectionUuids),
                },
                payoutFigureSections,
                payoutFigures: {
                    ...state.payoutFigures,
                    [typedAction.payoutFigure.uuid]: dehydratePayoutFigure(typedAction.payoutFigure),
                },
            };
        }

        case LoansActionsEnum.LoanPayoutFigureValueSet: {
            const typedAction: ILoanPayoutFigureValueSetAction = action as ILoanPayoutFigureValueSetAction;

            return {
                ...state,
                payoutFigures: {
                    ...state.payoutFigures,
                    [typedAction.loanPayoutFigureUuid]: {
                        ...state.payoutFigures[typedAction.loanPayoutFigureUuid],
                        [typedAction.key]: typedAction.value,
                    },
                },
            };
        }

        case LoansActionsEnum.LoanPayoutFigureItemDelete: {
            const typedAction: ILoanPayoutFigureItemDeleteAction = action as ILoanPayoutFigureItemDeleteAction;

            const payoutFigureItem: ILoanPayoutFigureItem = state.payoutFigureItems[typedAction.itemUuid];
            const payoutFigureSection: ILoanPayoutFigureSection = state.payoutFigureSections[payoutFigureItem.payoutFigureSectionUuid];

            return {
                ...state,
                payoutFigureItems: _.omit(state.payoutFigureItems, typedAction.itemUuid),
                payoutFigureSectionItemUuids: {
                    ...state.payoutFigureSectionItemUuids,
                    [payoutFigureSection.uuid]: _.filter(state.payoutFigureSectionItemUuids[payoutFigureSection.uuid], (itemUuid: string) => itemUuid !== typedAction.itemUuid),
                },
                payoutFigures: {
                    ...state.payoutFigures,
                    [payoutFigureSection.payoutFigureUuid]: {
                        ...state.payoutFigures[payoutFigureSection.payoutFigureUuid],
                        balanceAmount: state.payoutFigures[payoutFigureSection.payoutFigureUuid].balanceAmount - payoutFigureItem.amount,
                    },
                },
            };
        }

        case LoansActionsEnum.LoanPayoutFigureItemSet: {
            const typedAction: ILoanPayoutFigureItemSetAction = action as ILoanPayoutFigureItemSetAction;

            const payoutFigureSectionItemUuids: string[] = state.payoutFigureSectionItemUuids[typedAction.item.payoutFigureSectionUuid] || [];
            payoutFigureSectionItemUuids.push(typedAction.item.uuid);
            const payoutFigureSection: ILoanPayoutFigureSection = state.payoutFigureSections[typedAction.item.payoutFigureSectionUuid];
            let balanceAmount: number = typedAction.item.amount;

            _.forEach(state.payoutFigureSectionUuids[payoutFigureSection.payoutFigureUuid], (sectionUuid: string) => {
                _.forEach(state.payoutFigureSectionItemUuids[sectionUuid], (itemUuid: string) => {
                    if (itemUuid !== typedAction.item.uuid) {
                        balanceAmount += state.payoutFigureItems[itemUuid].amount;
                    }
                });
            });

            return {
                ...state,
                payoutFigureItems: {
                    ...state.payoutFigureItems,
                    [typedAction.item.uuid]: typedAction.item,
                },
                payoutFigureSectionItemUuids: {
                    ...state.payoutFigureSectionItemUuids,
                    [typedAction.item.payoutFigureSectionUuid]: _.uniq(payoutFigureSectionItemUuids),
                },
                payoutFigures: {
                    ...state.payoutFigures,
                    [payoutFigureSection.payoutFigureUuid]: {
                        ...state.payoutFigures[payoutFigureSection.payoutFigureUuid],
                        balanceAmount,
                    },
                },
            };
        }

        case LoansActionsEnum.LoanPayoutFigureItemValueSet: {
            const typedAction: ILoanPayoutFigureItemValueSetAction = action as ILoanPayoutFigureItemValueSetAction;

            const payoutFigureItem: ILoanPayoutFigureItem = state.payoutFigureItems[typedAction.itemUuid];
            const payoutFigureSection: ILoanPayoutFigureSection = state.payoutFigureSections[payoutFigureItem.payoutFigureSectionUuid];
            let balanceAmount: number = state.payoutFigures[payoutFigureSection.payoutFigureUuid].balanceAmount;

            if (typedAction.key === 'amount') {
                balanceAmount = balanceAmount - payoutFigureItem.amount + (typedAction.value as number);
            }

            return {
                ...state,
                payoutFigureItems: {
                    ...state.payoutFigureItems,
                    [typedAction.itemUuid]: {
                        ...state.payoutFigureItems[typedAction.itemUuid],
                        [typedAction.key]: typedAction.value,
                    },
                },
                payoutFigures: {
                    ...state.payoutFigures,
                    [payoutFigureSection.payoutFigureUuid]: {
                        ...state.payoutFigures[payoutFigureSection.payoutFigureUuid],
                        balanceAmount,
                    },
                },
            };
        }

        case LoansActionsEnum.LoanPayoutFigureSectionDelete: {
            const typedAction: ILoanPayoutFigureSectionDeleteAction = action as ILoanPayoutFigureSectionDeleteAction;

            const payoutFigureSection: ILoanPayoutFigureSection = state.payoutFigureSections[typedAction.sectionUuid];
            const payoutFigureItems: IDictionary<ILoanPayoutFigureItem> = { ...state.payoutFigureItems };
            const payoutFigureSectionItemUuids: IDictionary<string[]> = { ...state.payoutFigureSectionItemUuids };
            let balanceAmount: number = state.payoutFigures[payoutFigureSection.payoutFigureUuid].balanceAmount;

            _.forEach(payoutFigureSectionItemUuids[typedAction.sectionUuid], (itemUuid: string) => {
                balanceAmount -= payoutFigureItems[itemUuid].amount;
                delete payoutFigureItems[itemUuid];
            });

            return {
                ...state,
                payoutFigureItems,
                payoutFigureSectionItemUuids: _.omit(state.payoutFigureSectionItemUuids, typedAction.sectionUuid),
                payoutFigureSectionUuids: {
                    ...state.payoutFigureSectionUuids,
                    [payoutFigureSection.payoutFigureUuid]: _.filter(state.payoutFigureSectionUuids[payoutFigureSection.payoutFigureUuid], (sectionUuid: string) => sectionUuid !== typedAction.sectionUuid),
                },
                payoutFigureSections: _.omit(state.payoutFigureSections, typedAction.sectionUuid),
                payoutFigures: {
                    ...state.payoutFigures,
                    [payoutFigureSection.payoutFigureUuid]: {
                        ...state.payoutFigures[payoutFigureSection.payoutFigureUuid],
                        balanceAmount,
                    },
                },
            };
        }

        case LoansActionsEnum.LoanPayoutFigureSectionSet: {
            const typedAction: ILoanPayoutFigureSectionSetAction = action as ILoanPayoutFigureSectionSetAction;

            const payoutFigureItems: IDictionary<ILoanPayoutFigureItem> = { ...state.payoutFigureItems };
            const payoutFigureSectionUuids: string[] = state.payoutFigureSectionUuids[typedAction.section.payoutFigureUuid] || [];
            const payoutFigureSectionItemUuids: string[] = state.payoutFigureSectionItemUuids[typedAction.section.uuid];
            let balanceAmount: number = 0;

            _.forEach(typedAction.section.items, (item: ILoanPayoutFigureItem) => {
                payoutFigureItems[item.uuid] = item;
                payoutFigureSectionItemUuids.push(item.uuid);
                balanceAmount += item.amount;
            });
            payoutFigureSectionUuids.push(typedAction.section.uuid);

            _.forEach(state.payoutFigureSectionUuids[typedAction.section.payoutFigureUuid], (sectionUuid: string) => {
                if (sectionUuid !== typedAction.section.uuid) {
                    _.forEach(state.payoutFigureSectionItemUuids[sectionUuid], (itemUuid: string) => {
                        balanceAmount += state.payoutFigureItems[itemUuid].amount;
                    });
                }
            });

            return {
                ...state,
                payoutFigureItems,
                payoutFigureSectionItemUuids: {
                    ...state.payoutFigureSectionItemUuids,
                    [typedAction.section.uuid]: payoutFigureSectionItemUuids,
                },
                payoutFigureSectionUuids: {
                    ...state.payoutFigureSectionUuids,
                    [typedAction.section.payoutFigureUuid]: _.uniq(payoutFigureSectionUuids),
                },
                payoutFigureSections: {
                    ...state.payoutFigureSections,
                    [typedAction.section.uuid]: dehydratePayoutFigureSection(typedAction.section),
                },
                payoutFigures: {
                    ...state.payoutFigures,
                    [typedAction.section.payoutFigureUuid]: {
                        ...state.payoutFigures[typedAction.section.payoutFigureUuid],
                        balanceAmount,
                    },
                },
            };
        }

        case LoansActionsEnum.LoanPayoutFiguresSet: {
            const typedAction: ILoanPayoutFiguresSetAction = action as ILoanPayoutFiguresSetAction;

            const loanPayoutFigureUuids: string[] = state.loanPayoutFigureUuids[typedAction.loanUuid] || [];
            const payoutFigures: IDictionary<ILoanPayoutFigure> = { ...state.payoutFigures };
            const payoutFigureItems: IDictionary<ILoanPayoutFigureItem> = { ...state.payoutFigureItems };
            const payoutFigureSections: IDictionary<ILoanPayoutFigureSection> = { ...state.payoutFigureSections };
            const payoutFigureSectionItemUuids: IDictionary<string[]> = { ...state.payoutFigureSectionItemUuids };
            const payoutFigureSectionUuids: IDictionary<string[]> = { ...state.payoutFigureSectionItemUuids };

            _.forEach(typedAction.payoutFigures, (payoutFigure: ILoanPayoutFigure) => {
                payoutFigureSectionUuids[payoutFigure.uuid] = [];
                _.forEach(payoutFigure.sections, (section: ILoanPayoutFigureSection) => {
                    payoutFigureSectionItemUuids[section.uuid] = [];
                    _.forEach(section.items, (item: ILoanPayoutFigureItem) => {
                        payoutFigureItems[item.uuid] = item;
                        payoutFigureSectionItemUuids[section.uuid].push(item.uuid);
                    });
                    payoutFigureSections[section.uuid] = {
                        ...dehydratePayoutFigureSection(section),
                    };
                    payoutFigureSectionUuids[payoutFigure.uuid].push(section.uuid);
                });
                payoutFigures[payoutFigure.uuid] = {
                    ...dehydratePayoutFigure(payoutFigure),
                };
                loanPayoutFigureUuids.push(payoutFigure.uuid);
            });

            return {
                ...state,
                loanPayoutFigureUuids: {
                    ...state.loanPayoutFigureUuids,
                    [typedAction.loanUuid]: _.uniq(loanPayoutFigureUuids),
                },
                payoutFigureItems,
                payoutFigureSectionItemUuids,
                payoutFigureSectionUuids,
                payoutFigureSections,
                payoutFigures,
            };
        }

        case LoansActionsEnum.LoanTransactionsSet: {
            const typedAction: ILoanTransactionsSetAction = action as ILoanTransactionsSetAction;

            return {
                ...state,
                loanTransactions: {
                    ...state.loanTransactions,
                    [typedAction.loanUuid]: typedAction.loanTransactions,
                },
            };
        }

        case LoansActionsEnum.LoanValueSet: {
            const typedAction: ILoanValueSetAction = action as ILoanValueSetAction;

            return {
                ...state,
                loans: {
                    ...state.loans,
                    [typedAction.uuid]: {
                        ...state.loans[typedAction.uuid],
                        [typedAction.key]: typedAction.value,
                    },
                },
            };
        }

        case LoansActionsEnum.LoanWarehouseLoansSet: {
            const typedAction: ILoanWarehouseLoansSetAction = action as ILoanWarehouseLoansSetAction;
            const loanWarehouseLoanUuids: IDictionary<string[]> = { ...state.loanWarehouseLoanUuids };

            loanWarehouseLoanUuids[typedAction.loanUuid] = [];

            _.each(typedAction.warehouseLoans, (warehouseLoan: IWarehouseLoan) => {
                loanWarehouseLoanUuids[typedAction.loanUuid].push(warehouseLoan.uuid);
            });

            return {
                ...state,
                loanWarehouseLoanUuids,
            };
        }

        case LoansActionsEnum.LoansDashboardSet: {
            const typedAction: ILoansDashboardSetAction = action as ILoansDashboardSetAction;

            return {
                ...state,
                ...statifyLoans(state, typedAction.loans),
                loansDashboardUuids: _.map(typedAction.loans, (loan: ILoan) => loan.uuid),
            };
        }

        case LoansActionsEnum.LoansDischargeForecastSet: {
            const typedAction: ILoansDischargeForecastSetAction = action as ILoansDischargeForecastSetAction;

            const loanWarehouseLoanUuids: IDictionary<string[]> = {};

            _.each(typedAction.loans, (loan: ILoan) => {
                loanWarehouseLoanUuids[loan.uuid] = _.map(loan.warehouseLoans, (warehouseLoan: IWarehouseLoan) => warehouseLoan.uuid);
            });

            return {
                ...state,
                ...statifyLoans(state, typedAction.loans),
                loanWarehouseLoanUuids: {
                    ...state.loanWarehouseLoanUuids,
                    ...loanWarehouseLoanUuids,
                },
                loansDischargeForecastUuids: _.map(typedAction.loans, (loan: ILoan) => loan.uuid),
            };
        }

        case LoansActionsEnum.LoansDrawdownsPendingSet: {
            const typedAction: ILoansDrawdownsPendingSetAction = action as ILoansDrawdownsPendingSetAction;

            return {
                ...state,
                ...statifyDrawdowns(state, typedAction.drawdowns),
                drawdownPendingUuids: _.map(typedAction.drawdowns, (drawdown: ILoanDrawdown) => drawdown.uuid),
            };
        }

        case LoansActionsEnum.LoansDrawdownsSet: {
            const typedAction: ILoansDrawdownsSetAction = action as ILoansDrawdownsSetAction;

            return {
                ...state,
                ...statifyLoans(state, typedAction.loans),
                drawdownUuids: _.map(typedAction.loans, (loan: ILoan) => loan.uuid),
            };
        }

        case LoansActionsEnum.LoansOtherRetainedFundsSet: {
            const typedAction: ILoansOtherRetainedFundsSetAction = action as ILoansOtherRetainedFundsSetAction;

            return {
                ...state,
                ...statifyLoans(state, typedAction.loans),
                otherRetainedFundsUuids: _.map(typedAction.loans, (loan: ILoan) => loan.uuid),
            };
        }

        case LoansActionsEnum.LoansPaginatedList: {
            return {
                ...state,
                loansPaginatedLoading: true,
                loansPaginatedUuids: null,
            };
        }

        case LoansActionsEnum.LoansPaginatedSet: {
            const typedAction: ILoansPaginatedSetAction = action as ILoansPaginatedSetAction;

            return {
                ...state,
                ...statifyLoans(state, typedAction.loans),
                loansPaginatedCount: typedAction.loansPaginatedCount,
                loansPaginatedLoading: false,
                loansPaginatedUuids: _.map(typedAction.loans, (loan: ILoan) => loan.uuid),
            };
        }

        case LoansActionsEnum.LoansSearchResultsSet: {
            const typedAction: ILoansSearchResultsSetAction = action as ILoansSearchResultsSetAction;

            const loansSearchResultUuids: string[] = _.map(typedAction.loans, (loan: ILoan) => loan.uuid);

            return {
                ...state,
                ...statifyLoans(state, typedAction.loans),
                loansSearchResultUuids,
            };
        }

        case LoansActionsEnum.LoansSearchResultsClear: {
            return {
                ...state,
                loansSearchResultUuids: [],
            };
        }

        case WarehousesActionsEnum.WarehouseEligibleLoansSet: {
            const typedAction: IWarehouseEligibleLoansSetAction = action as IWarehouseEligibleLoansSetAction;

            return {
                ...state,
                ...statifyLoans(state, _.map(typedAction.eligibleLoans, (warehouseEligibleLoan: IWarehouseEligibleLoan) => warehouseEligibleLoan.loan)),
            };
        }

        case WarehousesActionsEnum.WarehouseLoanSet: {
            const typedAction: IWarehouseLoanSetAction = action as IWarehouseLoanSetAction;

            return {
                ...state,
                ...statifyLoan(state, typedAction.warehouseLoan.loan),
            };
        }

        case WarehousesActionsEnum.WarehouseLoansSet: {
            const typedAction: IWarehouseLoansSetAction = action as IWarehouseLoansSetAction;

            return {
                ...state,
                ...statifyLoans(state, _.map(typedAction.warehouseLoans, (warehouseLoan: IWarehouseLoan) => warehouseLoan.loan)),
            };
        }

        case WarehousesActionsEnum.WarehouseProposedSalesPendingSet: {
            const typedAction: IWarehouseProposedSalesPendingSetAction = action as IWarehouseProposedSalesPendingSetAction;

            return {
                ...state,
                ...statifyLoans(state, _.map(typedAction.warehouseProposedSales, (warehouseProposedSale: IWarehouseProposedSale) => warehouseProposedSale.sourceWarehouseLoan.loan)),
            };
        }

        case WarehousesActionsEnum.WarehousePendingDrawdownsSet: {
            const typedAction: IWarehousePendingDrawdownsSetAction = action as IWarehousePendingDrawdownsSetAction;

            return {
                ...state,
                ...statifyDrawdowns(state, typedAction.drawdowns),
            };
        }

        case LoansActionsEnum.LoanInternalComments: {
            const typedAction: ILoanInternalCommentsAction = action as ILoanInternalCommentsAction;

            return {
                ...state,
                loans: {
                    ...state.loans,
                    [typedAction.loanUuid]: {
                        ...state.loans[typedAction.loanUuid],
                        internalComments: typedAction.internalComments,
                    },
                },
            };
        }

        case LoansActionsEnum.LoanDrawdownInternalComments: {
            const typedAction: ILoanDrawdownInternalCommentsAction = action as ILoanDrawdownInternalCommentsAction;

            return {
                ...state,
                drawdowns: {
                    ...state.drawdowns,
                    [typedAction.drawdownUuid]: {
                        ...state.drawdowns[typedAction.drawdownUuid],
                        internalComments: typedAction.internalComments,
                    },
                },
            };
        }

        default:
            return state;
    }
}
